import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Icon } from "../Icon";
import { COMPANY } from "../data";
import { useCart } from "../cart";
import { PageHero, useSeo } from "../ui";
import { formatPrice, isBuyable } from "./Products";

/* 대표 사진 1장 + 아래 작은 사진 최대 5장 */
const MAX_IMAGES = 6;

function Gallery({ images, name }) {
  const [failed, setFailed] = useState(() => new Set());
  const usable = images.filter((src) => !failed.has(src)).slice(0, MAX_IMAGES);
  const [idx, setIdx] = useState(0);
  const markFailed = (src) => setFailed((prev) => new Set(prev).add(src));

  if (usable.length === 0) {
    return (
      <div className="pd__hero pd__hero--empty">
        <Icon.Box width="40" height="40" aria-hidden="true" focusable="false" />
      </div>
    );
  }

  const current = usable[Math.min(idx, usable.length - 1)];

  return (
    <div>
      <div className="pd__hero">
        <img src={current} alt={name} onError={() => markFailed(current)} />
      </div>
      {usable.length > 1 && (
        <div className="pd__thumbs">
          {usable.map((src, i) => (
            <button
              key={src}
              type="button"
              className={`pd__thumb ${src === current ? "is-active" : ""}`}
              onClick={() => setIdx(i)}
              aria-label={`${name} 사진 ${i + 1} 크게 보기`}
              aria-pressed={src === current}
            >
              <img src={src} alt="" loading="lazy" onError={() => markFailed(src)} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function BuyBox({ product }) {
  const navigate = useNavigate();
  const { add, requireLogin } = useCart();
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const soldout = product.status === "SOLDOUT";
  const buyable = isBuyable(product);
  const maxQty = product.stock != null && product.stock > 0 ? Math.min(product.stock, 999) : 999;

  const change = (n) => setQty((v) => Math.max(1, Math.min(maxQty, n(v))));

  const addToCart = () => requireLogin(() => {
    add(product, qty);
    setAdded(true);
  });

  const buyNow = () => requireLogin(() => {
    navigate("/checkout", {
      state: {
        items: [{
          slug: product.slug,
          name: product.name,
          price: product.price,
          price_note: product.price_note || "",
          thumb: (product.images && product.images[0]) || null,
          category: product.category || "",
          qty,
        }],
      },
    });
  });

  return (
    <div className="pd__buy">
      <div className="pd__price">{formatPrice(product.price, product.price_note)}</div>
      {product.stock != null && !soldout && <p className="pd__stock">재고 {product.stock}개</p>}

      {soldout ? (
        <>
          <p className="pd__stock">현재 품절입니다. 입고 시점을 알려드리겠습니다.</p>
          <Link to="/contact" className="btn btn--ghost btn--block">입고 문의하기</Link>
        </>
      ) : !buyable ? (
        <Link to="/contact" className="btn btn--primary btn--block">
          구매 문의하기 <Icon.ArrowRight width="16" height="16" aria-hidden="true" focusable="false" />
        </Link>
      ) : (
        <>
          <div className="pd__qty-row">
            <span className="pd__qty-label">수량</span>
            <div className="qty">
              <button type="button" onClick={() => change((v) => v - 1)} disabled={qty <= 1} aria-label="수량 줄이기">−</button>
              <input
                type="number"
                min="1"
                max={maxQty}
                value={qty}
                onChange={(e) => change(() => parseInt(e.target.value, 10) || 1)}
                aria-label="수량"
              />
              <button type="button" onClick={() => change((v) => v + 1)} disabled={qty >= maxQty} aria-label="수량 늘리기">+</button>
            </div>
          </div>
          <div className="pd__total">
            <span>총 상품금액</span>
            <strong>{(product.price * qty).toLocaleString("ko-KR")}원</strong>
          </div>
          <div className="pd__actions">
            <button type="button" className="btn btn--ghost" onClick={addToCart}>
              <Icon.Cart width="16" height="16" aria-hidden="true" focusable="false" /> 장바구니 담기
            </button>
            <button type="button" className="btn btn--primary" onClick={buyNow}>
              바로구매
            </button>
          </div>
          {added && (
            <p className="pd__added" role="status">
              장바구니에 담았습니다. <Link to="/cart">장바구니 보기 →</Link>
            </p>
          )}
        </>
      )}

      <ul className="pd__links">
        {product.manual && (
          <li>
            <a href={product.manual.url} download>
              <Icon.Download width="15" height="15" aria-hidden="true" focusable="false" />
              설명서 다운로드
              <span className="pd__links-meta">{product.manual.name}</span>
            </a>
          </li>
        )}
        {product.datasheet_url && (
          <li>
            <a href={product.datasheet_url} target="_blank" rel="noopener noreferrer">
              <Icon.Doc width="15" height="15" aria-hidden="true" focusable="false" />
              데이터시트 · 핀맵
            </a>
          </li>
        )}
        {product.resource_url && (
          <li>
            <a href={product.resource_url} target="_blank" rel="noopener noreferrer">
              <Icon.Code width="15" height="15" aria-hidden="true" focusable="false" />
              예제 레포 · 프로젝트 템플릿
            </a>
          </li>
        )}
        <li>
          <Link to="/refund">
            <Icon.Doc width="15" height="15" aria-hidden="true" focusable="false" />
            교환 · 환불 안내
          </Link>
        </li>
        <li>
          <a href={`mailto:${COMPANY.email}`}>
            <Icon.Mail width="15" height="15" aria-hidden="true" focusable="false" />
            {COMPANY.email}
          </a>
        </li>
      </ul>

      <p className="pd__note">세금계산서 발행 가능합니다. 수량이 많으면 단가를 협의해 드립니다.</p>
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const [state, setState] = useState({ status: "loading", data: null });

  useEffect(() => {
    let alive = true;
    setState({ status: "loading", data: null });
    (async () => {
      try {
        const res = await fetch(`/api/products/${encodeURIComponent(slug)}`);
        if (!res.ok) throw new Error("not found");
        const data = await res.json();
        if (alive) setState({ status: "ready", data });
      } catch {
        if (alive) setState({ status: "missing", data: null });
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  const p = state.data;

  useSeo({
    title: p ? `${p.name} | I.META 판매제품` : "판매제품 | I.META",
    description: p ? (p.summary || p.tagline || `${p.name} — I.META 판매제품`) : undefined,
    path: `/products/${slug}`,
  });

  if (state.status === "loading") {
    return (
      <section className="section">
        <p className="note-line">불러오는 중…</p>
      </section>
    );
  }

  if (state.status === "missing") {
    return (
      <PageHero kicker="PRODUCTS" title="상품을 찾을 수 없습니다" desc="판매가 끝났거나 주소가 바뀐 상품입니다.">
        <div className="hero__actions">
          <Link to="/products" className="btn btn--primary">
            판매제품 목록으로 <Icon.ArrowRight width="16" height="16" aria-hidden="true" focusable="false" />
          </Link>
        </div>
      </PageHero>
    );
  }

  const hasDetail = (p.description || "").trim() || (p.specs && p.specs.length > 0);

  return (
    <section className="section shop">
      <nav className="pd__back" aria-label="위치">
        <Link to="/products">판매제품</Link>
        {p.category && (
          <>
            {" / "}
            <Link to={`/products?cat=${encodeURIComponent(p.category)}`}>{p.category}</Link>
          </>
        )}
      </nav>

      <div className="pd">
        <div className="pd__media">
          <Gallery images={p.images || []} name={p.name} />
        </div>

        <div className="pd__info">
          {p.category && <div className="kicker">{p.category}</div>}
          <h1 className="pd__title">{p.name}</h1>
          {p.code && <div className="pd__code">상품코드 {p.code}</div>}
          {p.tagline && <p className="pd__tagline">{p.tagline}</p>}
          {p.summary && <p className="pd__summary">{p.summary}</p>}

          {p.features && p.features.length > 0 && (
            <ul className="checklist pd__features">
              {p.features.map((f) => (
                <li key={f}>
                  <Icon.Check width="15" height="15" aria-hidden="true" focusable="false" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          )}

          <BuyBox product={p} />
        </div>
      </div>

      {hasDetail && (
        <div className="pd__detail">
          <h2 className="pd__sub">상품 상세</h2>
          {p.description && <p className="pd__desc">{p.description}</p>}
          {p.specs && p.specs.length > 0 && (
            <>
              <h2 className="pd__sub">사양</h2>
              <div className="pd__specs">
                {p.specs.map((row) => (
                  <div key={row.label} className="about__meta-row">
                    <span>{row.label}</span><strong>{row.value}</strong>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}
